import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { coerceDate, getBrowserTimeZone } from "./time";

// Pure, timezone-aware enumeration of the local calendar days an instant
// interval touches. Used by the mobile agendas to "fan out" a multi-day or
// cross-midnight item (an external all-day busy block, a session that runs past
// midnight) under EVERY local day it overlaps — matching what react-big-calendar
// already does on desktop, where a multi-day event spans every day cell.
//
// Design notes (see docs/decisions.md "PR-B multi-day bucketing"):
//   * DST-correct. We never assume a day is 86_400_000 ms. Each day boundary is
//     resolved from a string YYYY-MM-DD incremented by one and converted back to
//     a real UTC instant via fromZonedTime (the same approach as the exported
//     nextLocalMidnight helper below, which marks-render now reuses). A naive
//     getTime()+24h walk is wrong on spring-forward / fall-back days.
//   * Half-open intervals, NO endExclusive flag. A day [dayStart, dayEnd) is
//     emitted iff [start, end) overlaps it (dayStart < end). Because the loop
//     never does -1ms arithmetic, BOTH end conventions in the codebase collapse
//     to the same day set: Google all-day's exclusive next-midnight end
//     (calendar-sync) AND the 1ms-trimmed end (calendar-events.js). An interval
//     ending exactly at local midnight therefore does NOT occupy the next day.
//   * No maxDays truncation. Callers that are month-scoped pass windowStart /
//     windowEnd to clamp to the visible range — truncating to "first N days"
//     would silently hide a conflict on day N+1, the very bug this fixes.

function pad2(n) {
  return String(n).padStart(2, "0");
}

// The real UTC instant of local midnight (in `tz`) for the day containing
// `date`.
function startOfLocalDay(date, tz) {
  const ymd = formatInTimeZone(date, tz, "yyyy-MM-dd");
  return fromZonedTime(`${ymd}T00:00:00`, tz);
}

/**
 * The real UTC instant of the NEXT local midnight (in `tz`) after the local day
 * containing `date`. DST-safe — the delta is NOT assumed to be 24h (spring
 * forward = 23h, fall back = 25h). Accepts ANY instant, not just a midnight one.
 * THE canonical "advance to the next local day boundary" primitive — used by the
 * day enumerator, localDayBounds, the marks segment splitter (marks-render), and
 * the availability single-day resize/click clamps. A naive getTime()+24h walk is
 * wrong on a transition day.
 */
export function nextLocalMidnight(date, tz) {
  const ymd = formatInTimeZone(date, tz, "yyyy-MM-dd");
  const [y, m, d] = ymd.split("-").map((s) => parseInt(s, 10));
  const inc = new Date(Date.UTC(y, m - 1, d + 1));
  const nextYmd = `${inc.getUTCFullYear()}-${pad2(inc.getUTCMonth() + 1)}-${pad2(inc.getUTCDate())}`;
  return fromZonedTime(`${nextYmd}T00:00:00`, tz);
}

/**
 * The browser-local [windowStart, windowEnd) clamp for a month-scoped agenda
 * showing the month containing `date`. windowEnd is the 1st of the next month;
 * windowStart is the 1st of `date`'s month — EXCEPT when `date`'s month is the
 * current month AND today is past the 1st, where it advances to the start of
 * today so the agenda opens on what's still actionable. Past/future months
 * render in full.
 *
 * Browser-local BY DESIGN: the availability agenda renders in browser-local
 * time (react-big-calendar's basis) and buckets via bucketEventsByLocalDay's
 * browser-tz default, so the month bounds must use the same browser-local
 * `new Date(y, m, d)` construction to stay aligned (NOT a tz-aware instant).
 * `now` is injectable so callers/tests stay deterministic.
 *
 * @param {Date} date   any instant within the month to show
 * @param {Date} [now]  the reference "today" (defaults to new Date())
 * @returns {{ windowStart: Date, windowEnd: Date }}
 */
export function monthAgendaWindow(date, now = new Date()) {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const viewingCurrentMonth =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth();
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 1, 0, 0, 0, 0);
  const windowStart =
    viewingCurrentMonth && startOfToday.getTime() > monthStart.getTime()
      ? startOfToday
      : monthStart;
  return { windowStart, windowEnd: monthEnd };
}

/**
 * Enumerate the local days an instant interval touches.
 *
 * @param {object} opts
 * @param {Date|string|number} opts.start  interval start (UTC instant)
 * @param {Date|string|number} opts.end    interval end (UTC instant; treated as
 *   a half-open boundary). Missing/<= start is treated as a point at `start`.
 * @param {string} [opts.timeZone]         IANA zone for day boundaries
 *   (defaults to the browser zone — matches each agenda's current basis).
 * @param {Date|string|number} [opts.windowStart] optional inclusive clamp
 * @param {Date|string|number} [opts.windowEnd]   optional exclusive clamp
 * @returns {{dayKey: string, dayStart: Date, dayEnd: Date}[]} one entry per
 *   covered local day, chronological. `dayKey` is yyyy-MM-dd in `timeZone`;
 *   dayEnd of one day === dayStart of the next.
 */
export function enumerateOverlappedDays({
  start,
  end,
  timeZone,
  windowStart = null,
  windowEnd = null,
} = {}) {
  const tz = timeZone || getBrowserTimeZone();
  const s = coerceDate(start);
  if (!s) return [];
  let e = coerceDate(end);
  // Inverted, zero-duration, or missing end → emit just the start day. Nudge the
  // boundary 1ms past start so the half-open loop emits exactly one day.
  if (!e || e.getTime() <= s.getTime()) {
    e = new Date(s.getTime() + 1);
  }

  const ws = coerceDate(windowStart);
  const we = coerceDate(windowEnd);
  // Clip the INTERVAL itself to the optional [windowStart, windowEnd) window
  // BEFORE enumerating days, so an event that doesn't actually overlap the
  // window contributes no days even if its calendar day would (e.g. an 08:00
  // event with a 12:00 windowStart). Enumeration then walks only the clipped
  // span — which also naturally skips out-of-window leading days. The clip is
  // correct for any window, not just day-aligned ones.
  const effStartMs = ws ? Math.max(s.getTime(), ws.getTime()) : s.getTime();
  const effEndMs = we ? Math.min(e.getTime(), we.getTime()) : e.getTime();
  if (effEndMs <= effStartMs) return [];

  const out = [];
  let cursor = startOfLocalDay(new Date(effStartMs), tz);
  // Defensive bound: real intervals are short and callers clamp via the window;
  // this only guards against a pathological/garbage end far in the future.
  let guard = 0;
  while (cursor.getTime() < effEndMs && guard < 750) {
    guard += 1;
    const next = nextLocalMidnight(cursor, tz);
    out.push({
      dayKey: formatInTimeZone(cursor, tz, "yyyy-MM-dd"),
      dayStart: cursor,
      dayEnd: next,
    });
    cursor = next;
  }
  return out;
}

/**
 * The yyyy-MM-dd LOCAL calendar-day key (in `timeZone`) for an instant. The
 * single source of truth for day-bucketing keys across the availability month
 * grid, its conflict/hidden pips (utils/overlap.js, MonthDateHeader,
 * AvailabilityPage.hiddenByDay) and the mobile agenda — so a near-midnight
 * event can never land on different cells across surfaces, and so the key
 * always matches the BROWSER-local cell react-big-calendar actually renders.
 */
export function localDayKey(date, timeZone) {
  const tz = timeZone || getBrowserTimeZone();
  const d = coerceDate(date);
  if (!d) return null;
  return formatInTimeZone(d, tz, "yyyy-MM-dd");
}

/**
 * The [dayStart, dayEnd) local-day window (and key) containing `date`. dayEnd
 * is the next local midnight (DST-safe). Returns null for invalid input. Reads
 * clearly at call sites that need a single day's bounds without relying on the
 * point-interval nudge inside enumerateOverlappedDays.
 */
export function localDayBounds(date, timeZone) {
  const tz = timeZone || getBrowserTimeZone();
  const d = coerceDate(date);
  if (!d) return null;
  const dayStart = startOfLocalDay(d, tz);
  return {
    dayKey: formatInTimeZone(dayStart, tz, "yyyy-MM-dd"),
    dayStart,
    dayEnd: nextLocalMidnight(dayStart, tz),
  };
}

/**
 * Bucket events into the LOCAL days they overlap, fanning a multi-day /
 * cross-midnight span under EVERY local day it touches and clipping each row
 * to its day. The shared primitive behind both the desktop month-view conflict
 * /hidden pips (utils/overlap.js, AvailabilityPage) and the mobile agenda —
 * ONE definition of "which day does this event belong to", so the surfaces can
 * never disagree.
 *
 * @param {object} opts
 * @param {Array} opts.events  RBC-shaped events ({ start, end, allDay?, resource? }).
 * @param {string} [opts.timeZone]  day-boundary zone (defaults to browser).
 * @param {Date|string|number} [opts.windowStart]  optional inclusive clamp.
 * @param {Date|string|number} [opts.windowEnd]    optional exclusive clamp.
 * @returns {Map<string, {dayKey: string, dayStart: Date, dayEnd: Date, rows: Array}>}
 *   one entry per touched local day. Each row is { event, clippedStart: Date,
 *   clippedEnd: Date, isContinuation: boolean, isAllDay: boolean, dayKey:
 *   string }: the ORIGINAL event reference is preserved (callbacks stay
 *   canonical) and clip bounds are the event's intersection with [dayStart,
 *   dayEnd). Consumers that render multiple days sort groups/rows by dayStart.
 */
export function bucketEventsByLocalDay({
  events = [],
  timeZone,
  windowStart = null,
  windowEnd = null,
} = {}) {
  const tz = timeZone || getBrowserTimeZone();
  const byDay = new Map();
  for (const e of events) {
    if (!e || !e.start || !e.end) continue;
    const s = coerceDate(e.start);
    const en = coerceDate(e.end);
    if (!s || !en) continue;
    const startMs = s.getTime();
    const endMs = en.getTime();
    const isAllDay = Boolean(e.allDay || e.resource?.allDay);
    const days = enumerateOverlappedDays({
      start: e.start,
      end: e.end,
      timeZone: tz,
      windowStart,
      windowEnd,
    });
    for (const d of days) {
      if (!byDay.has(d.dayKey)) {
        byDay.set(d.dayKey, {
          dayKey: d.dayKey,
          dayStart: d.dayStart,
          dayEnd: d.dayEnd,
          rows: [],
        });
      }
      const isContinuation = startMs < d.dayStart.getTime();
      const clippedStart = isContinuation ? d.dayStart : new Date(startMs);
      const clippedEnd = endMs < d.dayEnd.getTime() ? new Date(endMs) : d.dayEnd;
      byDay.get(d.dayKey).rows.push({
        event: e,
        clippedStart,
        clippedEnd,
        isContinuation,
        isAllDay,
        dayKey: d.dayKey,
      });
    }
  }
  return byDay;
}
